// RecentWins - Hub card showing recent completed tasks
// Three states: collapsed (header only), preview (3 wins), full (timeline modal)
// Celebrates accomplishments with actual task names

import React, { useState } from 'react';
import { CheckCircle, ChevronRight, ChevronDown, ChevronUp, Trophy, MoreVertical } from 'lucide-react';
import { ABLE_COLORS } from '../../constants';
import useLocalStorage from '../../hooks/useLocalStorage';

const STORAGE_KEY = 'mabel-recent-wins-expanded';
const PREVIEW_COUNT = 3;

export default function RecentWins({ entries = [], onOpenFull, onClearWins }) {
  const [isExpanded, setIsExpanded] = useLocalStorage(STORAGE_KEY, false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Filter to just completed tasks
  const completedTasks = entries.filter(e => e.type === 'task_completed');
  const previewTasks = completedTasks.slice(0, PREVIEW_COUNT);

  // Toggle expand/collapse
  const handleToggle = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  // Handle clear wins
  const handleClearWins = () => {
    setShowConfirm(false);
    setMenuOpen(false);
    if (onClearWins) onClearWins();
  };

  // Open full timeline
  const handleOpenFull = () => {
    onOpenFull();
  };

  // Empty state - always show collapsed style
  if (completedTasks.length === 0) {
    return (
      <div
        style={{
          width: '100%',
          padding: '14px 16px',
          background: ABLE_COLORS.neutral,
          border: 'none',
          borderRadius: ABLE_COLORS.cardRadius,
          boxShadow: ABLE_COLORS.cardShadow,
          textAlign: 'left'
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          color: ABLE_COLORS.textMuted
        }}>
          <Trophy size={20} color={ABLE_COLORS.border} />
          <span style={{ fontSize: '14px' }}>
            Complete a task to see your wins here
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        padding: '14px 16px',
        background: ABLE_COLORS.neutral,
        border: 'none',
        borderRadius: ABLE_COLORS.cardRadius,
        boxShadow: ABLE_COLORS.cardShadow,
        textAlign: 'left'
      }}
    >
      {/* Header row - always visible */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        {/* Left side - icon + title + expand toggle */}
        <button
          onClick={handleToggle}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            flex: 1
          }}
        >
          <Trophy size={20} color={ABLE_COLORS.accent} />
          <span style={{
            fontSize: '14px',
            fontWeight: '600',
            color: ABLE_COLORS.textPrimary
          }}>
            Recent Wins
          </span>
          {isExpanded ? (
            <ChevronUp size={16} color={ABLE_COLORS.textMuted} />
          ) : (
            <ChevronDown size={16} color={ABLE_COLORS.textMuted} />
          )}
        </button>

        {/* Right side - menu + view all */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', position: 'relative' }}>
          {/* Three-dot menu */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen(!menuOpen);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              background: 'none',
              border: 'none',
              padding: '4px',
              cursor: 'pointer',
              borderRadius: ABLE_COLORS.radiusSmall,
              color: ABLE_COLORS.textMuted
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = ABLE_COLORS.hoverBg}
            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
          >
            <MoreVertical size={16} />
          </button>

          {/* Dropdown menu */}
          {menuOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '4px',
                background: 'white',
                border: `1px solid ${ABLE_COLORS.border}`,
                borderRadius: ABLE_COLORS.radiusSmall,
                boxShadow: ABLE_COLORS.cardShadow,
                zIndex: 1000,
                minWidth: '160px'
              }}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen(false);
                  setShowConfirm(true);
                }}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: 'none',
                  border: 'none',
                  textAlign: 'left',
                  fontSize: '13px',
                  color: ABLE_COLORS.textPrimary,
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.target.style.background = ABLE_COLORS.hoverBg}
                onMouseLeave={(e) => e.target.style.background = 'none'}
              >
                Clear from view
              </button>
            </div>
          )}

          {/* View all button */}
          <button
            onClick={handleOpenFull}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              background: 'none',
              border: 'none',
              padding: '4px 8px',
              cursor: 'pointer',
              borderRadius: ABLE_COLORS.radiusSmall,
              color: ABLE_COLORS.textMuted,
              fontSize: '12px',
              transition: 'background 0.15s ease'
            }}
            onMouseEnter={(e) => e.target.style.background = ABLE_COLORS.hoverBg}
            onMouseLeave={(e) => e.target.style.background = 'none'}
          >
            <span>View all</span>
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* Preview tasks - only when expanded */}
      {isExpanded && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          marginTop: '12px',
          paddingTop: '12px',
          borderTop: `1px solid ${ABLE_COLORS.borderLight}`
        }}>
          {previewTasks.map((task, idx) => (
            <div
              key={task.id || idx}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}
            >
              <CheckCircle 
                size={16} 
                color={ABLE_COLORS.semantic.success}
                style={{ flexShrink: 0 }}
              />
              <span style={{
                fontSize: '13px',
                color: ABLE_COLORS.textPrimary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {task.title}
              </span>
            </div>
          ))}

          {/* Show count if more than preview */}
          {completedTasks.length > PREVIEW_COUNT && (
            <button
              onClick={handleOpenFull}
              style={{
                marginTop: '4px',
                fontSize: '12px',
                color: ABLE_COLORS.primary,
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                textAlign: 'left'
              }}
            >
              +{completedTasks.length - PREVIEW_COUNT} more wins →
            </button>
          )}
        </div>
      )}

      {/* Confirmation dialog */}
      {showConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
          onClick={() => setShowConfirm(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: ABLE_COLORS.cardRadius,
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: ABLE_COLORS.textPrimary,
                marginBottom: '8px'
              }}>
                Clear recent wins from view?
              </div>
              <div style={{
                fontSize: '14px',
                color: ABLE_COLORS.textSecondary,
                lineHeight: '1.4'
              }}>
                Tasks stay in your history - this just clears the display.
              </div>
            </div>
            <div style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowConfirm(false)}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: ABLE_COLORS.textSecondary,
                  background: 'none',
                  border: `1px solid ${ABLE_COLORS.border}`,
                  borderRadius: ABLE_COLORS.radiusSmall,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleClearWins}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  background: ABLE_COLORS.primary,
                  border: 'none',
                  borderRadius: ABLE_COLORS.radiusSmall,
                  cursor: 'pointer'
                }}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
